'use client';
import React, { useEffect, useRef, useState } from 'react';
import { BottomSheet } from '@/components/primitives/bottomsheet';
import Typography from '@/components/primitives/typography';
import CommentForm, { type CommentFormRef } from '@/screen/feed/view/components/comment-form';
import { useComments } from '@/screen/feed/view/hooks/useComments';
import CommentList from '@/screen/feed/view/components/comment-list';
import { XIcon } from 'lucide-react';
import styles from './styles.module.scss';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  feedId?: string;
  desktopRail?: boolean;
}

export interface DesktopCommentRailViewProps {
  isOpen: boolean;
  onClose: () => void;
  children?: React.ReactNode;
}

export const DesktopCommentRailView = ({
  isOpen,
  onClose,
  children,
}: DesktopCommentRailViewProps) => (
  <aside className={`${styles.rail} ${isOpen ? styles.railOpen : ''}`} aria-hidden={!isOpen}>
    {isOpen && (
      <>
        <div className={styles.railHeader}>
          <Typography className={styles.title}>댓글</Typography>
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="댓글 닫기">
            <XIcon size={20} />
          </button>
        </div>
        <div className={styles.railContent}>{children}</div>
      </>
    )}
  </aside>
);

const CommentActionSheet = (props: Props) => {
  const {
    isOpen,
    onClose,
    feedId,
    desktopRail = false,
  } = props;
  const commentFormRef = useRef<CommentFormRef>(null);
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 1024px)');
    const updateLayout = () => setIsDesktop(mediaQuery.matches);
    updateLayout();
    mediaQuery.addEventListener('change', updateLayout);
    return () => mediaQuery.removeEventListener('change', updateLayout);
  }, []);

  const {
    comments,
    isCommentsLoading,
    isLoadingMore,
    hasMoreComments,
    newComment,
    isCommentSubmitting,
    editingComment,
    onChangeComment,
    onSubmitComment,
    onEdit,
    onDelete,
    onCancelEdit,
    onUpdateComment,
    onChangeEditingComment,
    onLoadMoreComments,
  } = useComments(feedId, () => commentFormRef.current?.clear());

  const content = (
    <>
      <CommentForm
        ref={commentFormRef}
        newComment={newComment}
        isCommentSubmitting={isCommentSubmitting}
        onChangeComment={onChangeComment}
        onSubmitComment={onSubmitComment}
      />
      <CommentList
        comments={comments}
        isCommentsLoading={isCommentsLoading}
        isLoadingMore={isLoadingMore}
        hasMoreComments={hasMoreComments}
        editingComment={editingComment}
        isCommentSubmitting={isCommentSubmitting}
        onEdit={onEdit}
        onDelete={onDelete}
        onCancelEdit={onCancelEdit}
        onUpdateComment={onUpdateComment}
        onChangeEditingComment={onChangeEditingComment}
        onLoadMoreComments={onLoadMoreComments}
      />
    </>
  );

  if (desktopRail && isDesktop) {
    return (
      <DesktopCommentRailView isOpen={isOpen} onClose={onClose}>
        {content}
      </DesktopCommentRailView>
    );
  }

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      header={(
        <div className={styles.header}>
          <Typography className={styles.title}>댓글</Typography>
        </div>
      )}
    >
      {content}
    </BottomSheet>
  );
};

export default CommentActionSheet;
